/**
 * /superadmin/tenants — the cross-tenant directory.
 *
 * Searchable (name/slug ILIKE) + status-filterable + paginated list of
 * every customer tenant. Maps 1:1 to GET /api/v1/admin/tenants
 * (?status=&q=&limit=&offset=). Each row links to the Tenant 360
 * (/superadmin/tenants/{id}).
 *
 * Filters are local state; "Apply" commits the draft to the query (so
 * typing doesn't refetch per keystroke). keepPreviousData keeps the
 * table stable while a new page/filter is in flight.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { adminConsoleApi, toApiError } from "@/lib/api";
import type { AdminTenantQuery } from "@/types/admin-console";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/shared/status-badge";

export const Route = createFileRoute("/_authgated/superadmin/tenants/")({
  component: TenantDirectoryRoute,
});

const PAGE_SIZE = 50;

// Mirrors models.SubscriptionStatus — the exact set the backend's
// `?status=` does an exact match on. "all" clears the filter.
const STATUS_OPTIONS = [
  "all",
  "trial",
  "active",
  "past_due",
  "cancelled",
  "expired",
] as const;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function TenantDirectoryRoute() {
  const [searchDraft, setSearchDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState<string>("all");
  const [applied, setApplied] = useState<{ q: string; status: string }>({
    q: "",
    status: "all",
  });
  const [offset, setOffset] = useState(0);

  const query: AdminTenantQuery = useMemo(
    () => ({
      q: applied.q || undefined,
      status: applied.status === "all" ? undefined : applied.status,
      limit: PAGE_SIZE,
      offset,
    }),
    [applied, offset],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-tenants", query],
    queryFn: ({ signal }) => adminConsoleApi.listTenants(query, signal),
    placeholderData: keepPreviousData,
  });

  function applyFilters() {
    setOffset(0);
    setApplied({ q: searchDraft.trim(), status: statusDraft });
  }

  function clearFilters() {
    setSearchDraft("");
    setStatusDraft("all");
    setApplied({ q: "", status: "all" });
    setOffset(0);
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;
  const hasFilters = applied.q !== "" || applied.status !== "all";

  return (
    <>
      <PageHeader
        title="Tenants"
        description="Every customer workspace. Search, filter, and open a tenant to view its 360 and grant features."
      />

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="grid gap-4 py-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="t-search">Search</Label>
            <Input
              id="t-search"
              placeholder="Name or slug"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-status">Status</Label>
            <Select value={statusDraft} onValueChange={setStatusDraft}>
              <SelectTrigger id="t-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "all" ? "All Statuses" : s.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={applyFilters} disabled={isFetching}>
              <Search className="h-4 w-4" />
              Apply
            </Button>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh"
              className="ml-auto"
            >
              <RefreshCw
                className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="flex flex-col items-center gap-3 py-16">
              <Alert variant="destructive" className="max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Couldn&apos;t load tenants</AlertTitle>
                <AlertDescription>
                  {toApiError(error).message}
                </AlertDescription>
              </Alert>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            </div>
          ) : isLoading ? (
            <div className="space-y-2 p-4">
              {["a", "b", "c", "d", "e", "f", "g", "h"].map((k) => (
                <Skeleton key={k} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={Building2}
                title={
                  hasFilters ? "No tenants match" : "No Tenants Yet"
                }
                description={
                  hasFilters
                    ? "Try a different name, slug, or status filter."
                    : "Customer workspaces appear here once they register."
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((t) => (
                    <TableRow key={t.id} className="cursor-pointer">
                      <TableCell className="font-medium">
                        <Link
                          to="/superadmin/tenants/$id"
                          params={{ id: String(t.id) }}
                          className="block hover:text-primary"
                        >
                          {t.name}
                          {!t.is_active && (
                            <span className="ml-2 text-xs font-normal text-destructive">
                              inactive
                            </span>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <Link
                          to="/superadmin/tenants/$id"
                          params={{ id: String(t.id) }}
                          className="block"
                        >
                          {t.slug}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Link
                          to="/superadmin/tenants/$id"
                          params={{ id: String(t.id) }}
                          className="block"
                        >
                          {t.plan_name ?? (
                            <span className="text-muted-foreground">
                              —
                            </span>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/superadmin/tenants/$id"
                          params={{ id: String(t.id) }}
                          className="block"
                        >
                          <StatusBadge status={t.subscription_status} />
                        </Link>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {t.employee_count}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {fmtDate(t.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!error && items.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground tabular-nums">
            {pageStart}–{pageEnd} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev || isFetching}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext || isFetching}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
